import { startTransition, useDeferredValue, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import {
  ChevronDown,
  ChevronRight,
  Eye,
  LogOut,
  Menu,
  MessageCircleMore,
  MessageSquareText,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Search,
  Send,
  Sparkles,
  X,
} from 'lucide-react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { api } from '@/api/client'
import { MessengerLinkSheet } from '@/components/messenger-link-sheet'
import { WazzupPanel } from '@/components/wazzup-panel'
import type { BootstrapPayload } from '@/types/api'
import { Button, Input } from '@/components/ui'
import { navIcons, resolveBackendIcon } from '@/lib/icons'
import { cn } from '@/lib/utils'

type SectionKey = 'catalog' | 'currentSquares' | 'directories' | 'reports'

function suggestionRows(payload?: Awaited<ReturnType<typeof api.search>>) {
  if (!payload) {
    return []
  }

  const records = payload.schemas.flatMap((schema) =>
    schema.records.slice(0, 3).map((record) => ({
      id: record.id,
      label: `${schema.name} · #${record.id}`,
      path: record.path,
    })),
  )

  return records.slice(0, 5)
}

function normalizePath(path: string) {
  if (path !== '/' && path.endsWith('/')) {
    return path.slice(0, -1)
  }
  return path
}

function matchesPath(currentPath: string, targetPath?: string | null) {
  if (!targetPath) {
    return false
  }

  const normalizedCurrent = normalizePath(currentPath)
  const normalizedTarget = normalizePath(targetPath.split('?')[0] ?? '')

  return (
    normalizedCurrent === normalizedTarget ||
    (normalizedTarget !== '/' && normalizedCurrent.startsWith(`${normalizedTarget}/`))
  )
}

function isSpaNavigationPath(path: string) {
  const pathname = normalizePath(path.split('?')[0] ?? path)
  return (
    pathname === '/' ||
    pathname === '/admin/partner-cabinets' ||
    pathname === '/admin/partner-cabinets/new' ||
    /^\/admin\/partner-cabinets\/\d+$/.test(pathname) ||
    pathname === '/profile' ||
    pathname === '/search' ||
    pathname.startsWith('/square/') ||
    pathname.startsWith('/current-square/') ||
    pathname.startsWith('/application/') ||
    pathname.startsWith('/create-application/') ||
    pathname === '/catalog/import' ||
    pathname === '/reports' ||
    pathname.startsWith('/reports/category/') ||
    pathname.startsWith('/reports/create') ||
    /^\/reports\/\d+/.test(pathname) ||
    pathname === '/directories' ||
    pathname.startsWith('/directories/') ||
    pathname.startsWith('/directory-entry/')
  )
}

function initialSectionState(pathname: string) {
  return {
    catalog: pathname.startsWith('/catalog'),
    currentSquares: pathname.startsWith('/current-square'),
    directories: pathname.startsWith('/directories'),
    reports: pathname.startsWith('/reports'),
  }
}

function extractApplicationId(pathname: string) {
  const match = normalizePath(pathname).match(/^\/application\/(\d+)(?:\/|$)/)
  return match ? Number(match[1]) : null
}

export function AppShell({
  bootstrap,
  onLogout,
}: {
  bootstrap: BootstrapPayload
  onLogout: () => Promise<void>
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchQuery, setSearchQuery] = useState(
    new URLSearchParams(location.search).get('query') ?? '',
  )
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [openMessengerSheet, setOpenMessengerSheet] = useState<'telegram' | 'max' | null>(null)
  const [wazzupOpen, setWazzupOpen] = useState(false)
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>(
    initialSectionState(location.pathname),
  )
  const deferredSearchQuery = useDeferredValue(searchQuery)
  const isCompactSidebar = sidebarCollapsed && !mobileSidebarOpen
  const isCurrentSquareWorkspace = location.pathname.startsWith('/current-square/')
  const userJobTitle = bootstrap.user.jobTitle ?? bootstrap.user.groups?.join(', ') ?? null
  const wazzupApplicationId = extractApplicationId(location.pathname)

  useEffect(() => {
    setSearchQuery(new URLSearchParams(location.search).get('query') ?? '')
  }, [location.search])

  useEffect(() => {
    setMobileSidebarOpen(false)
    setOpenSections((current) => ({
      ...current,
      ...initialSectionState(location.pathname),
    }))
  }, [location.pathname])

  useEffect(() => {
    const saved = window.localStorage.getItem('crm-sidebar-collapsed')
    setSidebarCollapsed(saved === 'true')
  }, [])

  useEffect(() => {
    window.localStorage.setItem('crm-sidebar-collapsed', String(sidebarCollapsed))
  }, [sidebarCollapsed])

  const suggestionsQuery = useQuery({
    queryKey: ['search-suggestions', deferredSearchQuery],
    queryFn: () => api.search(deferredSearchQuery),
    enabled: !isCurrentSquareWorkspace && deferredSearchQuery.trim().length > 1,
  })

  const activeSchemaIds = useMemo(() => {
    const activeIds = new Set<number>()
    bootstrap.navigation.schemas.forEach((schema) => {
      if (
        matchesPath(location.pathname, schema.detailPath) ||
        schema.initialStages.some((stage) => matchesPath(location.pathname, stage.path))
      ) {
        activeIds.add(schema.id)
      }
    })
    return activeIds
  }, [bootstrap.navigation.schemas, location.pathname])

  const toggleSection = (section: SectionKey) => {
    if (isCompactSidebar) {
      setSidebarCollapsed(false)
      return
    }
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }))
  }

  if (isCurrentSquareWorkspace) {
    return (
      <div className="min-h-screen bg-shell">
        <a
          className="sr-only left-4 top-4 z-50 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-ink-950 focus:not-sr-only focus:absolute"
          href="#crm-main"
        >
          К содержимому
        </a>

        <main className="flex min-h-screen flex-col px-3 py-3 lg:px-4 lg:py-4" id="crm-main">
          <Outlet />
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-shell">
      <a
        className="sr-only left-4 top-4 z-50 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-ink-950 focus:not-sr-only focus:absolute"
        href="#crm-main"
      >
        К содержимому
      </a>

      <div
        aria-hidden={!mobileSidebarOpen}
        className={cn(
          'fixed inset-0 z-30 bg-ink-950/45 transition lg:hidden',
          mobileSidebarOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={() => setMobileSidebarOpen(false)}
      />

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-56 flex-col border-r border-white/10 bg-ink-950 text-white transition-all duration-200',
          mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          isCompactSidebar ? 'lg:w-[6.5rem]' : 'lg:w-56',
        )}
      >
        <div className="border-b border-white/10 px-4 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1 space-y-3">
              <a
                className={cn(
                  'flex min-w-0 items-center gap-3 rounded-2xl text-white transition hover:bg-white/5',
                  isCompactSidebar ? 'justify-center px-0 py-2' : 'px-2 py-2',
                )}
                href="/"
                title="Рабочий стол"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10 text-sm font-bold">
                  CRM
                </div>
                {isCompactSidebar ? null : (
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-white">Рабочее пространство</p>
                    <p className="truncate text-xs text-white/45">CRM</p>
                  </div>
                )}
              </a>

              <div
                className={cn(
                  'rounded-[24px] border border-white/10 bg-white/5 p-2.5',
                  isCompactSidebar && 'border-transparent bg-transparent p-0',
                )}
              >
                <div className={cn('flex items-center gap-3', isCompactSidebar && 'justify-center')}>
                  <UserAvatar
                    avatarUrl={bootstrap.user.avatarUrl}
                    name={bootstrap.user.fullName}
                  />
                  {isCompactSidebar ? null : (
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-white">{bootstrap.user.fullName}</p>
                      {userJobTitle ? (
                        <p className="truncate text-xs text-white/50">{userJobTitle}</p>
                      ) : null}
                    </div>
                  )}
                </div>

                <div className={cn('mt-2.5', isCompactSidebar && 'mt-3')}>
                  <SidebarNavLink
                    collapsed={isCompactSidebar}
                    icon={navIcons.profile}
                    label="Профиль"
                    title="Профиль"
                    to="/profile"
                  />
                </div>
              </div>
            </div>

            <button
              className="rounded-2xl p-2 text-white/70 transition hover:bg-white/10 hover:text-white lg:hidden"
              onClick={() => setMobileSidebarOpen(false)}
              type="button"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <nav className="crm-scrollbar flex-1 space-y-1 overflow-y-auto px-3 py-4">
          <SidebarNavLink
            collapsed={isCompactSidebar}
            icon={navIcons.dashboard}
            label="Рабочий стол"
            title="Рабочий стол"
            to="/"
          />

          {bootstrap.navigation.training.visible && bootstrap.navigation.training.path ? (
            <SidebarAnchorLink
              active={matchesPath(location.pathname, bootstrap.navigation.training.path)}
              collapsed={isCompactSidebar}
              href={bootstrap.navigation.training.path}
              icon={navIcons.training}
              label="Обучение"
            />
          ) : null}

          {bootstrap.navigation.directories.length ? (
            <SidebarDisclosure
              active={openSections.directories}
              collapsed={isCompactSidebar}
              icon={navIcons.directories}
              label="Справочники"
              onToggle={() => toggleSection('directories')}
            >
              {bootstrap.navigation.directories.map((directory) => (
                <SidebarAnchorLink
                  active={matchesPath(location.pathname, directory.path)}
                  collapsed={false}
                  href={directory.path}
                  icon={navIcons.directories}
                  key={directory.id}
                  label={directory.name}
                  nested
                />
              ))}
            </SidebarDisclosure>
          ) : null}

          {bootstrap.navigation.catalog.visible ? (
            <SidebarDisclosure
              active={openSections.catalog}
              collapsed={isCompactSidebar}
              icon={navIcons.catalog}
              label="Каталог"
              onToggle={() => toggleSection('catalog')}
            >
              {bootstrap.navigation.catalog.items.map((item) => (
                <SidebarAnchorLink
                  active={matchesPath(location.pathname, item.path)}
                  collapsed={false}
                  href={item.path}
                  icon={navIcons.catalog}
                  key={item.key}
                  label={item.label}
                  nested
                />
              ))}
            </SidebarDisclosure>
          ) : null}

          <SidebarDisclosure
            active={openSections.reports}
            collapsed={isCompactSidebar}
            icon={navIcons.reports}
            label="Отчёты"
            onToggle={() => toggleSection('reports')}
          >
            <SidebarAnchorLink
              active={
                matchesPath(location.pathname, '/reports/') &&
                !matchesPath(location.pathname, '/reports/city-analytics') &&
                !matchesPath(location.pathname, '/reports/city-applications') &&
                new URLSearchParams(location.search).get('category') !== 'uncategorized'
              }
              collapsed={false}
              href="/reports/"
              icon={navIcons.reports}
              label="Все отчёты"
              nested
            />
            {bootstrap.navigation.reports.cityAnalyticsPath ? (
              <SidebarAnchorLink
                active={matchesPath(location.pathname, bootstrap.navigation.reports.cityAnalyticsPath)}
                collapsed={false}
                href={bootstrap.navigation.reports.cityAnalyticsPath}
                icon={navIcons.reports}
                label="Аналитика по городу"
                nested
              />
            ) : null}
            {bootstrap.navigation.reports.cityApplicationsPath ? (
              <SidebarAnchorLink
                active={matchesPath(location.pathname, bootstrap.navigation.reports.cityApplicationsPath)}
                collapsed={false}
                href={bootstrap.navigation.reports.cityApplicationsPath}
                icon={navIcons.reports}
                label="Заявки по городу"
                nested
              />
            ) : null}
            {bootstrap.navigation.reports.createPath ? (
              <SidebarAnchorLink
                active={matchesPath(location.pathname, bootstrap.navigation.reports.createPath)}
                collapsed={false}
                href={bootstrap.navigation.reports.createPath}
                icon={navIcons.reports}
                label="Создать отчёт"
                nested
              />
            ) : null}
            {bootstrap.navigation.reports.categories.map((category) => (
              <SidebarAnchorLink
                active={
                  category.id === 'uncategorized'
                    ? matchesPath(location.pathname, '/reports/') &&
                      new URLSearchParams(location.search).get('category') === 'uncategorized'
                    : matchesPath(location.pathname, category.path)
                }
                collapsed={false}
                href={category.path}
                icon={navIcons.reports}
                key={category.id}
                label={`${category.name} (${category.count})`}
                nested
              />
            ))}
          </SidebarDisclosure>

          <SidebarDivider />
          <SidebarSectionHeading collapsed={isCompactSidebar} label="Текущие квадраты" />

          {bootstrap.navigation.currentSquares.length ? (
            <SidebarDisclosure
              active={openSections.currentSquares}
              collapsed={isCompactSidebar}
              icon={navIcons.currentSquares}
              label="Работа на планшете"
              onToggle={() => toggleSection('currentSquares')}
            >
              {bootstrap.navigation.currentSquares.map((square) => (
                <SidebarAnchorLink
                  active={matchesPath(location.pathname, square.path)}
                  collapsed={false}
                  href={square.path ?? `/current-square/${square.id}`}
                  icon={navIcons.currentSquares}
                  key={square.id}
                  label={square.name}
                  nested
                />
              ))}
            </SidebarDisclosure>
          ) : null}

          <SidebarDivider />
          <SidebarSectionHeading collapsed={isCompactSidebar} label="Доступные схемы" />

          {bootstrap.navigation.schemas.length ? (
            <div className="space-y-1">
              {bootstrap.navigation.schemas.map((schema) => (
                <SchemaMenuItem
                  active={activeSchemaIds.has(schema.id)}
                  collapsed={isCompactSidebar}
                  icon={resolveBackendIcon(schema.icon, navIcons.schemas)}
                  key={schema.id}
                  onRequestExpand={() => setSidebarCollapsed(false)}
                  schema={schema}
                  title={schema.name}
                />
              ))}
            </div>
          ) : null}

          {bootstrap.navigation.admin.visible ? (
            <>
              {bootstrap.navigation.admin.partnerCabinetsPath ? (
                <SidebarAnchorLink
                  active={matchesPath(location.pathname, bootstrap.navigation.admin.partnerCabinetsPath)}
                  collapsed={isCompactSidebar}
                  href={bootstrap.navigation.admin.partnerCabinetsPath}
                  icon={navIcons.admin}
                  label="Партнерские кабинеты"
                />
              ) : null}
              {bootstrap.navigation.admin.schemaListPath ? (
                <SidebarAnchorLink
                  active={matchesPath(location.pathname, bootstrap.navigation.admin.schemaListPath)}
                  collapsed={isCompactSidebar}
                  href={bootstrap.navigation.admin.schemaListPath}
                  icon={navIcons.admin}
                  label="Настройки схем"
                />
              ) : null}
              {bootstrap.navigation.admin.squareGroupsPath ? (
                <SidebarAnchorLink
                  active={matchesPath(location.pathname, bootstrap.navigation.admin.squareGroupsPath)}
                  collapsed={isCompactSidebar}
                  href={bootstrap.navigation.admin.squareGroupsPath}
                  icon={navIcons.squareGroups}
                  label="Управление квадратами"
                />
              ) : null}
              {bootstrap.navigation.admin.settingsPath ? (
                <SidebarAnchorLink
                  active={matchesPath(location.pathname, bootstrap.navigation.admin.settingsPath)}
                  collapsed={isCompactSidebar}
                  href={bootstrap.navigation.admin.settingsPath}
                  icon={navIcons.admin}
                  label="Общие настройки"
                />
              ) : null}
            </>
          ) : null}
        </nav>

        <div className="border-t border-white/10 p-3 lg:hidden">
          <button
            className="flex w-full items-center justify-center rounded-2xl bg-white/5 px-4 py-3 text-sm font-semibold text-white/75 transition hover:bg-white/10 hover:text-white"
            onClick={() => {
              setMobileSidebarOpen(false)
              void onLogout()
            }}
            type="button"
          >
            <LogOut className="h-4 w-4" />
            <span className="ml-2">Выйти</span>
          </button>
        </div>

        <div className="hidden border-t border-white/10 p-3 lg:block">
          <button
            className="flex w-full items-center justify-center rounded-2xl bg-white/5 px-4 py-3 text-sm font-semibold text-white/75 transition hover:bg-white/10 hover:text-white"
            onClick={() => setSidebarCollapsed((current) => !current)}
            type="button"
          >
            {isCompactSidebar ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            {isCompactSidebar ? null : <span className="ml-2">Свернуть</span>}
          </button>
        </div>
      </aside>

      <div
        className={cn(
          'min-h-screen transition-[padding-left] duration-200',
          isCompactSidebar ? 'lg:pl-[6.5rem]' : 'lg:pl-56',
        )}
      >
        <header className="sticky top-0 z-20 border-b border-white/70 bg-slate-100/90 px-4 py-4 backdrop-blur lg:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <button
                aria-label="Открыть меню"
                className="rounded-2xl bg-white px-3 py-3 text-ink-700 shadow-soft transition hover:bg-ink-50 lg:hidden"
                onClick={() => setMobileSidebarOpen(true)}
                type="button"
              >
                <Menu className="h-5 w-5" />
              </button>

              <div className="relative w-full max-w-2xl flex-1">
                <form
                  onSubmit={(event) => {
                    event.preventDefault()
                    startTransition(() => {
                      navigate(`/search?query=${encodeURIComponent(searchQuery.trim())}`)
                    })
                  }}
                >
                  <Input
                    className="pl-12 pr-12"
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Найти заявку, клиента или номер"
                    value={searchQuery}
                  />
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-400" />
                </form>

                {searchQuery.trim().length > 1 && suggestionRows(suggestionsQuery.data).length > 0 ? (
                  <div className="absolute left-0 right-0 top-[calc(100%+12px)] rounded-3xl border border-white/70 bg-white/95 p-2 shadow-panel">
                    {suggestionRows(suggestionsQuery.data).map((item) => (
                      <button
                        className="flex w-full items-center justify-between rounded-2xl px-3 py-3 text-left text-sm text-ink-700 transition hover:bg-ink-50"
                        key={`${item.path}-${item.id}`}
                        onClick={() => {
                          startTransition(() => {
                            navigate(item.path)
                          })
                        }}
                        type="button"
                      >
                        <span>{item.label}</span>
                        <Sparkles className="h-4 w-4 text-accent-500" />
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 lg:justify-end">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  className="min-w-[124px]"
                  onClick={() => setOpenMessengerSheet('telegram')}
                  tone="ghost"
                >
                  <Send className="mr-2 h-4 w-4" />
                  Telegram
                </Button>
                <Button
                  className="min-w-[108px]"
                  onClick={() => setOpenMessengerSheet('max')}
                  tone="ghost"
                >
                  <MessageCircleMore className="mr-2 h-4 w-4" />
                  MAX
                </Button>
                {bootstrap.user.wazzupEnabled ? (
                  <Button
                    className="min-w-[124px]"
                    onClick={() => setWazzupOpen(true)}
                    tone="ghost"
                  >
                    <MessageSquareText className="mr-2 h-4 w-4" />
                    Wazzup
                  </Button>
                ) : null}
              </div>

              <div className="flex min-w-0 items-center gap-3">
                <UserAvatar
                  avatarUrl={bootstrap.user.avatarUrl}
                  className="hidden lg:flex"
                  name={bootstrap.user.fullName}
                  tone="light"
                />
                <div className="min-w-0 text-right">
                  <p className="truncate text-sm font-semibold text-ink-900">{bootstrap.user.fullName}</p>
                  {userJobTitle ? (
                    <p className="truncate text-xs text-ink-500">{userJobTitle}</p>
                  ) : null}
                </div>
              </div>
              <Button className="hidden lg:inline-flex" onClick={() => void onLogout()} tone="ghost">
                <LogOut className="mr-2 h-4 w-4" />
                Выйти
              </Button>
            </div>
          </div>
        </header>

        <main className="px-4 py-5 lg:px-8 lg:py-8" id="crm-main">
          <Outlet />
        </main>
      </div>

      <MessengerLinkSheet
        kind="telegram"
        onClose={() => setOpenMessengerSheet(null)}
        open={openMessengerSheet === 'telegram'}
      />
      <MessengerLinkSheet
        kind="max"
        onClose={() => setOpenMessengerSheet(null)}
        open={openMessengerSheet === 'max'}
      />
      <WazzupPanel
        applicationId={wazzupApplicationId}
        onClose={() => setWazzupOpen(false)}
        open={wazzupOpen}
      />
    </div>
  )
}

function SidebarDisclosure({
  active,
  children,
  collapsed,
  icon: Icon,
  label,
  onToggle,
}: {
  active: boolean
  children: ReactNode
  collapsed: boolean
  icon: LucideIcon
  label: string
  onToggle: () => void
}) {
  return (
    <div className="space-y-1">
      <button
        className={cn(
          'flex w-full items-center rounded-2xl px-3 py-3 text-left text-sm font-semibold transition',
          active ? 'bg-white/10 text-white' : 'text-white/70 hover:bg-white/8 hover:text-white',
          collapsed && 'justify-center px-2',
        )}
        onClick={onToggle}
        title={label}
        type="button"
      >
        <Icon className="h-4 w-4 shrink-0" />
        {collapsed ? null : (
          <>
            <span className="ml-3 flex-1">{label}</span>
            {active ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </>
        )}
      </button>
      {!collapsed && active ? <div className="space-y-1">{children}</div> : null}
    </div>
  )
}

function SidebarDivider() {
  return <div className="my-3 border-t border-white/10" />
}

function SidebarSectionHeading({
  collapsed,
  label,
}: {
  collapsed: boolean
  label: string
}) {
  if (collapsed) {
    return null
  }

  return (
    <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/35">
      {label}
    </p>
  )
}

function getUserInitials(name: string) {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)

  if (!parts.length) {
    return 'CRM'
  }

  return parts.map((part) => part[0]?.toUpperCase() ?? '').join('')
}

function UserAvatar({
  avatarUrl,
  className,
  name,
  tone = 'dark',
}: {
  avatarUrl: string | null
  className?: string
  name: string
  tone?: 'dark' | 'light'
}) {
  const [failed, setFailed] = useState(false)
  const showImage = Boolean(avatarUrl) && !failed
  return (
    <div
      className={cn(
        'flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl text-sm font-semibold',
        tone === 'light' ? 'bg-ink-100 text-ink-700' : 'bg-white/10 text-white',
        className,
      )}
    >
      {showImage ? (
        <img
          alt={name}
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
          src={avatarUrl ?? undefined}
        />
      ) : (
        <span>{getUserInitials(name)}</span>
      )}
    </div>
  )
}

function SidebarNavLink({
  collapsed,
  icon: Icon,
  label,
  title,
  to,
}: {
  collapsed: boolean
  icon: LucideIcon
  label: string
  title: string
  to: string
}) {
  return (
    <NavLink
      className={({ isActive }) =>
        cn(
          'flex items-center rounded-2xl px-3 py-3 text-sm font-semibold transition',
          isActive ? 'bg-white text-ink-950 shadow-soft' : 'text-white/75 hover:bg-white/8 hover:text-white',
          collapsed && 'justify-center px-2',
        )
      }
      end={to === '/'}
      title={title}
      to={to}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {collapsed ? null : <span className="ml-3 truncate">{label}</span>}
    </NavLink>
  )
}

function SidebarAnchorLink({
  active,
  collapsed,
  href,
  icon: Icon,
  label,
  nested = false,
}: {
  active: boolean
  collapsed: boolean
  href: string
  icon: LucideIcon
  label: string
  nested?: boolean
}) {
  return (
    isSpaNavigationPath(href) ? (
      <NavLink
        className={cn(
          'flex items-center rounded-2xl px-3 py-3 text-sm font-medium transition',
          active ? 'bg-white text-ink-950 shadow-soft' : 'text-white/70 hover:bg-white/8 hover:text-white',
          collapsed && 'justify-center px-2',
          nested && 'ml-3 text-sm',
        )}
        title={label}
        to={href}
      >
        <Icon className="h-4 w-4 shrink-0" />
        {collapsed ? null : <span className="ml-3 truncate">{label}</span>}
      </NavLink>
    ) : (
      <a
        className={cn(
          'flex items-center rounded-2xl px-3 py-3 text-sm font-medium transition',
          active ? 'bg-white text-ink-950 shadow-soft' : 'text-white/70 hover:bg-white/8 hover:text-white',
          collapsed && 'justify-center px-2',
          nested && 'ml-3 text-sm',
        )}
        href={href}
        title={label}
      >
        <Icon className="h-4 w-4 shrink-0" />
        {collapsed ? null : <span className="ml-3 truncate">{label}</span>}
      </a>
    )
  )
}

function SchemaMenuItem({
  active,
  collapsed,
  icon: Icon,
  onRequestExpand,
  schema,
  title,
}: {
  active: boolean
  collapsed: boolean
  icon: LucideIcon
  onRequestExpand: () => void
  schema: BootstrapPayload['navigation']['schemas'][number]
  title: string
}) {
  const location = useLocation()
  const [open, setOpen] = useState(active)

  useEffect(() => {
    if (
      matchesPath(location.pathname, schema.detailPath) ||
      schema.initialStages.some((stage) => matchesPath(location.pathname, stage.path))
    ) {
      setOpen(true)
    }
  }, [location.pathname, schema.detailPath, schema.initialStages])

  return (
    <div className="space-y-1">
      <button
        className={cn(
          'flex w-full items-center rounded-2xl px-3 py-3 text-left text-sm font-semibold transition',
          active ? 'bg-white/10 text-white' : 'text-white/75 hover:bg-white/8 hover:text-white',
          collapsed && 'justify-center px-2',
        )}
        onClick={() => {
          if (collapsed) {
            onRequestExpand()
            return
          }
          setOpen((current) => !current)
        }}
        title={title}
        type="button"
      >
        <Icon className="h-4 w-4 shrink-0" />
        {collapsed ? null : (
          <>
            <span className="ml-3 flex-1 truncate">{schema.name}</span>
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </>
        )}
      </button>

      {!collapsed && open ? (
        <div className="space-y-1">
          {schema.initialStages.map((stage) => (
            <SidebarAnchorLink
              active={matchesPath(location.pathname, stage.path)}
              collapsed={false}
              href={stage.path}
              icon={Plus}
              key={stage.id}
              label={stage.label}
              nested
            />
          ))}
          <SidebarAnchorLink
            active={matchesPath(location.pathname, schema.detailPath)}
            collapsed={false}
            href={schema.detailPath}
            icon={Eye}
            label="Просмотреть схему"
            nested
          />
        </div>
      ) : null}
    </div>
  )
}
